"use client";

import { useState } from "react";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { A9entLogo } from "@/components/a9ent-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePublicAuthI18n } from "@/components/public-auth/i18n/public-auth-i18n-provider";
import { useAuth } from "@/lib/auth-context";
import { apiFetch, HTTP_STATUS_TOO_MANY_REQUESTS } from "@/lib/api-fetch";
import { appendLngParam } from "@/lib/public-auth/append-lng-param";

export function ContactUsForm() {
  const { user, isLoading: authLoading } = useAuth();
  const { t, locale } = usePublicAuthI18n();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitted(false);
    setIsLoading(true);

    try {
      const res = await apiFetch("/api/public/contact-us", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName,
          email,
          subject: subject.trim() || undefined,
          message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
          return;
        }
        const msg =
          typeof data?.message === "string"
            ? data.message
            : typeof data?.error === "string"
              ? data.error
              : t("contactUs.somethingWentWrong");
        setError(msg);
        return;
      }
      toast.success(data?.message || t("contactUs.successToast"));
      setSubmitted(true);
      setFullName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch {
      setError(t("contactUs.networkError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      <header className="border-b border-white/10 bg-black">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
          <Link href="/" className="flex items-center">
            <A9entLogo height={28} darkBackground className="h-5 w-auto" />
          </Link>
          <div className="flex min-h-9 items-center justify-end gap-1">
            {authLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-white/70" aria-label={t("common.loading")} />
            ) : user ? (
              user.onboardingComplete === false ? (
                <Link href="/onboarding">
                  <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white">
                    {t("common.continueOnboarding")}
                  </Button>
                </Link>
              ) : (
                <Link href="/admin">
                  <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white">
                    {t("common.dashboard")}
                  </Button>
                </Link>
              )
            ) : (
              <>
                <Link href={appendLngParam("/login", locale)}>
                  <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white">
                    {t("common.signIn")}
                  </Button>
                </Link>
                <Link href={appendLngParam("/signup", locale)}>
                  <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white">
                    {t("common.createAccount")}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-10 md:py-14">
        <div className="mx-auto max-w-xl space-y-8 scrollbar-thin">
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold tracking-tight text-white md:text-4xl">{t("contactUs.title")}</h1>
            <p className="mt-3 text-lg text-white/80">{t("contactUs.subtitle")}</p>
          </div>

          <div className="rounded-2xl border border-white/15 bg-black p-8 shadow-sm">
            {submitted && !error && (
              <div className="mb-4 rounded-lg bg-white/10 px-4 py-3 text-sm text-white">
                {t("contactUs.successBanner")}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {error ? (
                <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="contact-fullName">{t("contactUs.name")}</Label>
                <Input
                  id="contact-fullName"
                  type="text"
                  placeholder={t("contactUs.namePlaceholder")}
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email">{t("common.email")}</Label>
                <Input
                  id="contact-email"
                  type="email"
                  autoComplete="email"
                  placeholder={t("contactUs.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-subject">{t("contactUs.subjectOptional")}</Label>
                <Input
                  id="contact-subject"
                  type="text"
                  autoComplete="off"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={isLoading}
                  placeholder={t("contactUs.subjectPlaceholder")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message">{t("contactUs.message")}</Label>
                <Textarea
                  id="contact-message"
                  rows={6}
                  className="min-h-[140px] resize-y"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder={t("contactUs.messagePlaceholder")}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600 text-white transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.01] hover:shadow-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("contactUs.sending")}
                  </>
                ) : (
                  t("contactUs.sendMessage")
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
